import { useState, useEffect } from 'react';
import { HoldingsCompany, HoldingsCompensation, formatCents } from '../../types';
import { EMPLOYMENT_TYPES, CHANGE_TYPES } from '../../types/compensation';
import { Button, Input, Textarea } from "@repo/o37-ui";
import { X, DollarSign, ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface CompensationFormProps {
  personId: string;
  personName: string;
  compensation?: HoldingsCompensation | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function CompensationForm({
  personId,
  personName,
  compensation,
  onClose,
  onSaved
}: CompensationFormProps) {
  const isEditing = !!compensation;

  // Company selection
  const [companies, setCompanies] = useState<HoldingsCompany[]>([]);
  const [companyId, setCompanyId] = useState(compensation?.companyId || '');

  // Compensation breakdown (stored as dollars for input, converted to cents)
  const [baseSalary, setBaseSalary] = useState(
    compensation ? (compensation.baseSalaryCents / 100).toString() : ''
  );
  const [targetBonus, setTargetBonus] = useState(
    compensation ? (compensation.targetBonusCents / 100).toString() : ''
  );
  const [equityValue, setEquityValue] = useState(
    compensation ? (compensation.equityValueCents / 100).toString() : ''
  );
  // Benefits as line items
  const [benefitItems, setBenefitItems] = useState<{ name: string; amount: string }[]>(
    compensation?.benefitsBreakdown?.length
      ? compensation.benefitsBreakdown.map(b => ({ name: b.name, amount: (b.amountCents / 100).toString() }))
      : [{ name: 'Health Insurance', amount: '' }]
  );
  const [employerTaxes, setEmployerTaxes] = useState(
    compensation ? (compensation.employerTaxesCents / 100).toString() : ''
  );
  const [employerTaxRate, setEmployerTaxRate] = useState(
    compensation ? (compensation.employerTaxRate * 100).toString() : '7.65'
  );
  // Additional fees (Deel, payroll service, etc.)
  const [additionalFees, setAdditionalFees] = useState(
    compensation ? (compensation.additionalFeesCents / 100).toString() : ''
  );
  const [additionalFeesDescription, setAdditionalFeesDescription] = useState(
    compensation?.additionalFeesDescription || ''
  );

  // Employment details
  const [status, setStatus] = useState<string>(compensation?.status || 'active');
  const [effectiveDate, setEffectiveDate] = useState(
    compensation?.effectiveDate || new Date().toISOString().split('T')[0]
  );
  const [employmentType, setEmploymentType] = useState(compensation?.employmentType || 'full_time');
  const [title, setTitle] = useState(compensation?.title || '');
  const [department, setDepartment] = useState(compensation?.department || '');
  const [notes, setNotes] = useState(compensation?.notes || '');
  const [changeReason, setChangeReason] = useState('');

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [autoCalcTaxes, setAutoCalcTaxes] = useState(!compensation?.employerTaxesCents);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadCompanies();
  }, []);

  // Auto-calculate employer taxes when base salary or bonus changes
  useEffect(() => {
    if (autoCalcTaxes) {
      const base = parseFloat(baseSalary) || 0;
      const bonus = parseFloat(targetBonus) || 0;
      const rate = parseFloat(employerTaxRate) / 100 || 0.0765;
      const calculatedTaxes = (base + bonus) * rate;
      setEmployerTaxes(calculatedTaxes.toFixed(2));
    }
  }, [baseSalary, targetBonus, employerTaxRate, autoCalcTaxes]);

  const loadCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/companies`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.filter((c: any) => c.status === 'active').map((c: any) => ({
          id: c.id,
          name: c.name,
          status: c.status
        })));
      }
    } catch (e) {
      console.error('Failed to load companies:', e);
    }
  };

  // Calculate total benefits from line items
  const calculateBenefitsTotal = () => {
    return benefitItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const calculateTotal = () => {
    const base = parseFloat(baseSalary) || 0;
    const bonus = parseFloat(targetBonus) || 0;
    const equity = parseFloat(equityValue) || 0;
    const benefitsVal = calculateBenefitsTotal();
    const taxes = parseFloat(employerTaxes) || 0;
    const fees = parseFloat(additionalFees) || 0;
    return base + bonus + equity + benefitsVal + taxes + fees;
  };

  // Benefit item management
  const addBenefitItem = () => {
    setBenefitItems([...benefitItems, { name: '', amount: '' }]);
  };

  const removeBenefitItem = (index: number) => {
    setBenefitItems(benefitItems.filter((_, i) => i !== index));
  };

  const updateBenefitItem = (index: number, field: 'name' | 'amount', value: string) => {
    const updated = [...benefitItems];
    updated[index][field] = value;
    setBenefitItems(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyId) {
      setError('Please select a company');
      return;
    }

    const baseSalaryCents = Math.round((parseFloat(baseSalary) || 0) * 100);
    if (baseSalaryCents <= 0) {
      setError('Base salary is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Build benefits breakdown and calculate total
      const benefitsBreakdown = benefitItems
        .filter(b => b.name.trim() && parseFloat(b.amount) > 0)
        .map(b => ({
          name: b.name.trim(),
          amount_cents: Math.round((parseFloat(b.amount) || 0) * 100)
        }));
      const benefitsTotalCents = benefitsBreakdown.reduce((sum, b) => sum + b.amount_cents, 0);

      const payload = {
        person_id: personId,
        company_id: companyId,
        base_salary_cents: baseSalaryCents,
        target_bonus_cents: Math.round((parseFloat(targetBonus) || 0) * 100),
        equity_value_cents: Math.round((parseFloat(equityValue) || 0) * 100),
        benefits_cents: benefitsTotalCents,
        benefits_breakdown: benefitsBreakdown,
        employer_taxes_cents: Math.round((parseFloat(employerTaxes) || 0) * 100),
        employer_tax_rate: (parseFloat(employerTaxRate) || 7.65) / 100,
        additional_fees_cents: Math.round((parseFloat(additionalFees) || 0) * 100),
        additional_fees_description: additionalFeesDescription.trim() || null,
        status,
        effective_date: effectiveDate,
        employment_type: employmentType,
        title: title.trim() || null,
        department: department.trim() || null,
        notes: notes.trim() || null,
        change_reason: isEditing ? changeReason : null
      };

      const url = isEditing
        ? `${API_URL}/holdings/compensation/${compensation.id}`
        : `${API_URL}/holdings/compensation`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSaved();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save compensation');
      }
    } catch (e) {
      setError('Failed to save compensation');
    } finally {
      setSaving(false);
    }
  };

  const totalAnnual = calculateTotal();
  const monthlyTotal = totalAnnual / 12;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <div>
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit Compensation' : 'Add Compensation'}
            </h2>
            <p className="text-sm text-neutral-500 mt-1">{personName}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* Company Selection */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Paying Company *
            </label>
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select company...</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Employment Type & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Employment Type
              </label>
              <select
                value={employmentType}
                onChange={(e) => setEmploymentType(e.target.value as 'full_time' | 'part_time' | 'contractor' | 'intern')}
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {EMPLOYMENT_TYPES.map(t => (
                  <option key={t.type} value={t.type}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="pending">Pending Start</option>
                <option value="on_leave">On Leave</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
          </div>

          {/* Base Salary */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Base Salary (Annual) *
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                value={baseSalary}
                onChange={(e) => setBaseSalary(e.target.value)}
                placeholder="120000"
                type="number"
                min="0"
                step="any"
                className="pl-9"
                required
              />
            </div>
          </div>

          {/* Bonus */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Target Bonus (Annual)
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                value={targetBonus}
                onChange={(e) => setTargetBonus(e.target.value)}
                placeholder="20000"
                type="number"
                min="0"
                step="any"
                className="pl-9"
              />
            </div>
          </div>

          {/* Advanced Options Toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
          >
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showAdvanced ? 'Hide' : 'Show'} Advanced Options
          </button>

          {showAdvanced && (
            <div className="space-y-4 p-4 bg-neutral-50 rounded-lg">
              {/* Equity */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Equity Value (Annual)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <Input
                    value={equityValue}
                    onChange={(e) => setEquityValue(e.target.value)}
                    placeholder="30000"
                    type="number"
                    min="0"
                    step="any"
                    className="pl-9"
                  />
                </div>
                <p className="text-xs text-neutral-500 mt-1">Annualized value of equity grants</p>
              </div>

              {/* Benefits Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-neutral-700">
                    Benefits (Annual Employer Cost)
                  </label>
                  <button
                    type="button"
                    onClick={addBenefitItem}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                  >
                    <Plus className="w-3 h-3" />
                    Add Benefit
                  </button>
                </div>
                <div className="space-y-2">
                  {benefitItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={item.name}
                        onChange={(e) => updateBenefitItem(index, 'name', e.target.value)}
                        placeholder="Benefit name (e.g., Health Insurance)"
                        className="flex-1"
                      />
                      <div className="relative w-32">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                        <Input
                          value={item.amount}
                          onChange={(e) => updateBenefitItem(index, 'amount', e.target.value)}
                          placeholder="0"
                          type="number"
                          min="0"
                          step="any"
                          className="pl-9"
                        />
                      </div>
                      {benefitItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeBenefitItem(index)}
                          className="p-2 text-neutral-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  Total: {formatCents(Math.round(calculateBenefitsTotal() * 100))}
                </p>
              </div>

              {/* Employer Taxes */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-neutral-700">
                    Employer Taxes (Annual)
                  </label>
                  <label className="flex items-center gap-2 text-xs text-neutral-500">
                    <input
                      type="checkbox"
                      checked={autoCalcTaxes}
                      onChange={(e) => setAutoCalcTaxes(e.target.checked)}
                      className="rounded border-neutral-300"
                    />
                    Auto-calculate
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <Input
                      value={employerTaxes}
                      onChange={(e) => {
                        setAutoCalcTaxes(false);
                        setEmployerTaxes(e.target.value);
                      }}
                      placeholder="10710"
                      type="number"
                      min="0"
                      step="any"
                      className="pl-9"
                      disabled={autoCalcTaxes}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      value={employerTaxRate}
                      onChange={(e) => setEmployerTaxRate(e.target.value)}
                      type="number"
                      min="0"
                      max="100"
                      step="any"
                      className="w-20"
                      disabled={!autoCalcTaxes}
                    />
                    <span className="text-sm text-neutral-500">% rate</span>
                  </div>
                </div>
                <p className="text-xs text-neutral-500 mt-1">FICA, unemployment, etc. (~7.65% default)</p>
              </div>

              {/* Additional Fees (Deel, payroll service, etc.) */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Additional Fees (Annual)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <Input
                      value={additionalFees}
                      onChange={(e) => setAdditionalFees(e.target.value)}
                      placeholder="0"
                      type="number"
                      min="0"
                      step="any"
                      className="pl-9"
                    />
                  </div>
                  <Input
                    value={additionalFeesDescription}
                    onChange={(e) => setAdditionalFeesDescription(e.target.value)}
                    placeholder="e.g., Deel fees, payroll service"
                  />
                </div>
                <p className="text-xs text-neutral-500 mt-1">Payroll provider fees, EOR services (Deel, Remote, etc.)</p>
              </div>

              {/* Title & Department */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Job Title
                  </label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Senior Engineer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Department
                  </label>
                  <Input
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    placeholder="Engineering"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Effective Date */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Effective Date *
            </label>
            <Input
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              type="date"
              required
            />
          </div>

          {/* Change Reason (for edits) */}
          {isEditing && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Reason for Change
              </label>
              <select
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select reason...</option>
                {CHANGE_TYPES.filter(t => t.type !== 'initial').map(t => (
                  <option key={t.type} value={t.type}>{t.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional compensation details..."
              rows={2}
            />
          </div>

          {/* Total Preview */}
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-medium text-emerald-800">Total Annual Cost</div>
                <div className="text-xs text-emerald-600">Fully loaded employer cost</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-emerald-700">
                  {formatCents(Math.round(totalAnnual * 100))}
                </div>
                <div className="text-sm text-emerald-600">
                  {formatCents(Math.round(monthlyTotal * 100))}/mo
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} text="Cancel" />
            <Button
              type="submit"
              disabled={saving}
              text={saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Compensation'}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
